<template>
  <div class="user-page my-collection-page">
    <!-- Add Collection Modal -->
    <AppModal
      :max-height="'90vh'"
      :visible.sync="showAddCollectionModal"
      width="720px"
      @close="showAddCollectionModal = false"
    >
      <AddCollectionModal @close="showAddCollectionModal = false" @onSubmit="handleAddCollectionSubmit"/>
    </AppModal>

    <div class="uk-grid-medium" uk-grid>
      <div class="uk-width-1-1">
        <!-- Header -->
        <PageHeader :title="collectionTitle" icon="uil-notebooks">
          <template #right>
            <a v-if="canAddCollection"
               class="collection-add-button text-white btn btn-primary btn-sm transition-3d-hover"
               @click="openAddCollectionModal">
              <i class="uil-plus"></i>
              {{ $t("collection.add_collection") }}
            </a>
          </template>
        </PageHeader>

        <!-- Search & Filter -->
        <div class="section-header pb-0">
          <div class="section-header-left">
            <input v-model="search" :placeholder="$t('home.Education_shearing')" type="text" @input="isTyping = true">
          </div>
          <div class="section-header-right">
            <multiple-select-input :allow-empty="false"
                                   :model.sync="selectedOrderBy"
                                   :show-labels="false"
                                   :values="orderByOptions"
                                   class="order-by-select"
                                   label="label"
                                   track-by="value"
                                   @select="onOrderByChange"/>
          </div>
        </div>

        <!-- Alerts -->
        <div v-if="successMessage" class="uk-alert-success position-relative" uk-alert>
          <a class="uk-alert-close" uk-close @click="successMessage = null"></a>
          <p class="m-0" style="color: green">
            <i aria-hidden="true" class="fa fa-check d-inline-flex mr-2"></i>
            {{ successMessage }}
          </p>
        </div>

        <div v-if="errorMessage" class="uk-alert-danger bg-soft-danger position_relative" uk-alert>
          <a class="uk-alert-close" uk-close @click="errorMessage = null"></a>
          <p class="m-0" style="color: red">{{ errorMessage }}</p>
        </div>

        <!-- Tabs -->
        <collection-tabs :active-tab.sync="currentTab" :tabs="tabItems" @tab-change="handleTabChange"/>

        <!-- Collection Grid -->
        <collection-grid :collections="currentCollections" :empty-title="$t('general.no_result_found')"
                         :loading="collectionsLoading" @collection-click="goToCollectionDetail"/>

      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SecureLS from "secure-ls";
import _ from "lodash";

import { GET_ITEMS, LOADING } from "@/core/services/store/REST.module";
import AddCollectionModal from "@/view/components/educationDetail/AddCollectionModal.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";

import CollectionTabs from "./components/CollectionTabs.vue";
import CollectionGrid from "./components/CollectionGrid.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import AppModal from "@/view/components/ui/AppModal.vue";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });

const API_ENDPOINTS = {
  COLLECTION: 'api/collection',
  COLLECTION_ALL: 'api/collection-all'
};

const TAB_KEYS = {
  MY_COLLECTIONS: 'my_collections',
  FOLLOWING_COLLECTIONS: 'following_collections',
  ALL_COLLECTIONS: 'all_collections'
};

export default {
  name: "MyCollection",

  components: {
    AddCollectionModal,
    CollectionTabs,
    CollectionGrid,
    MultipleSelectInput,
    PageHeader,
    AppModal
  },

  data() {
    return {
      myCollections: [],
      followingCollections: [],
      allCollections: [],
      search: '',
      selectedOrderBy: null,
      isTyping: false,
      currentTab: TAB_KEYS.MY_COLLECTIONS,
      successMessage: null,
      errorMessage: null,
      menuList: [],
      initialLoading: true,
      showAddCollectionModal: false
    };
  },

  computed: {
    ...mapGetters({
      storeLoading: LOADING,
      institutionType: 'institutionType'
    }),

    canAddCollection() {
      if (this.institutionType === 'azcon') {
        return !!this.isAdmin;
      }
      return true;
    },

    collectionsLoading() {
      return this.initialLoading || this.storeLoading;
    },

    collectionTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find(item => item.type === 'my-collection');
      return this.getLocalizedWidgetTitle(menuItem, this.$t('home.My_collections'), this.$i18n?.locale);
    },

    tabItems() {
      return [
        { key: TAB_KEYS.MY_COLLECTIONS, label: this.$t('collection.my_collections') },
        { key: TAB_KEYS.FOLLOWING_COLLECTIONS, label: this.$t('collection.following_collections') },
        { key: TAB_KEYS.ALL_COLLECTIONS, label: this.$t('collection.all_collections') }
      ];
    },

    orderByOptions() {
      return [
        { value: 'name', label: this.$t('home.Filter_by_name') },
        { value: 'created_at', label: this.$t('home.Filter_by_date') }
      ];
    },

    orderBy() {
      return this.selectedOrderBy?.value || 'name';
    },

    currentCollections() {
      const collectionsMap = {
        [TAB_KEYS.MY_COLLECTIONS]: this.myCollections,
        [TAB_KEYS.FOLLOWING_COLLECTIONS]: this.followingCollections,
        [TAB_KEYS.ALL_COLLECTIONS]: this.allCollections
      };
      return collectionsMap[this.currentTab] || [];
    }
  },

  watch: {
    search: _.debounce(function () {
      this.isTyping = false;
    }, 500),

    isTyping(value) {
      if (!value) {
        this.applyFilter();
      }
    }
  },

  mounted() {
    this.initialize();
  },

  methods: {
    initialize() {
      this.loadMenuList();
      this.selectedOrderBy = this.orderByOptions[0];
      const tabFromQuery = this.$route.query.tab;
      if (tabFromQuery && Object.values(TAB_KEYS).includes(tabFromQuery)) {
        this.currentTab = tabFromQuery;
      }
      this.applyFilter();
    },

    onOrderByChange() {
      this.applyFilter();
    },

    loadMenuList() {
      const data = ls.get('dashboardMenuList');
      if (data?.menu) {
        this.menuList = data.menu;
      }
    },

    openAddCollectionModal() {
      this.showAddCollectionModal = true;
    },

    handleAddCollectionSubmit(payload) {
      this.showAddCollectionModal = false;

      if (payload.success) {
        this.successMessage = this.$t("collection.collection_saved_success");
        this.fetchCollections();
      } else {
        this.errorMessage = payload.message;
      }
    },

    goToCollectionDetail(id) {
      this.$router.push({
        name: 'user.collection-detail',
        params: { id }
      });
    },

    handleTabChange(tabKey) {
      this.currentTab = tabKey;
      const query = { ...this.$route.query, tab: tabKey };
      this.$router.replace({ query }).catch(() => {});
    },

    applyFilter() {
      const searchTerm = this.search || null;
      this.fetchCollections(searchTerm);
      this.fetchAllCollections(searchTerm);
    },

    async fetchCollections(search = null) {
      try {
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: API_ENDPOINTS.COLLECTION,
          filters: {
            search,
            order_by: this.orderBy
          },
          acceptPromise: false,
          showLoading: true
        });

        if (result?.data) {
          this.myCollections = result.data.my_collections || [];
          this.followingCollections = result.data.following_collections || [];
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
        this.errorMessage = this.$t('collection.fetch_error');
      } finally {
        this.initialLoading = false;
      }
    },

    async fetchAllCollections(search = null) {
      const sort = this.orderBy === 'name' ? 'asc' : null;

      try {
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: API_ENDPOINTS.COLLECTION_ALL,
          filters: {
            search,
            order_by: this.orderBy,
            sort
          },
          acceptPromise: false,
          showLoading: true
        });

        if (result?.data) {
          this.allCollections = result.data;
        }
      } catch (error) {
        console.error('Error fetching all collections:', error);
        this.errorMessage = this.$t('collection.fetch_error');
      }
    }
  }
};
</script>

<style scoped>
/* UIkit grid init sonrası eklenen margin'i sabitle — skeleton kaymasını önler */
.my-collection-page .uk-grid-medium > * {
  margin-top: 0 !important;
}

.my-collection-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.my-collection-page .collection-add-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  box-sizing: border-box;
}

.my-collection-page .section-header {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
}

.my-collection-page .section-header-left {
  flex: 1 1 auto;
  min-width: 0;
}

.my-collection-page .section-header-left input {
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 16px;
  line-height: 1.5;
}

.my-collection-page .section-header-left input:focus {
  border-color: var(--backgroundColor, #667eea);
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.my-collection-page .section-header-right {
  display: flex;
  align-items: center;
  align-self: center;
}

::v-deep .order-by-select.form-group,
::v-deep .order-by-select.form-group > .custom-multiselect {
  height: 48px !important;
  min-height: 48px;
  margin-bottom: 0 !important;
}

::v-deep .app-modal-body {
  padding: 0;
}

::v-deep .app-modal-body > .card {
  border: 0;
  border-radius: 16px;
  box-shadow: none;
}

::v-deep .app-modal-header {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  padding: 0;
  border: 0;
  background: transparent;
}

/* Order By Select Styles */
.order-by-select {
  min-width: 180px;
  margin-bottom: 0;
}

::v-deep .order-by-select .form-group {
  margin-bottom: 0;
}

::v-deep .order-by-select .multiselect__tags {
  height: 48px;
  min-height: 48px;
  padding: 8px 40px 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  font-size: 14px;
  transition: all 0.2s ease;
}

::v-deep .order-by-select .multiselect__tags:hover {
  border-color: #cbd5e1;
}

::v-deep .order-by-select .multiselect--active .multiselect__tags {
  border-color: var(--backgroundColor, #667eea);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

::v-deep .order-by-select .multiselect__single {
  font-size: 14px;
  color: #334155;
  margin-bottom: 0;
  padding: 0;
  line-height: 22px;
}

::v-deep .order-by-select .multiselect__select {
  height: 38px;
  width: 32px;
}

::v-deep .order-by-select .multiselect__content-wrapper {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

::v-deep .order-by-select .multiselect__option {
  padding: 10px 12px;
  font-size: 14px;
  color: #334155;
  min-height: 40px;
}

::v-deep .order-by-select .multiselect__option--highlight {
  background: #f1f5f9;
  color: #334155;
}

::v-deep .order-by-select .multiselect__option--selected {
  background: var(--backgroundColor, #667eea);
  color: #ffffff;
  font-weight: 500;
}

::v-deep .order-by-select .multiselect__option--selected.multiselect__option--highlight {
  background: var(--backgroundColor, #667eea);
  color: #ffffff;
}

@media (max-width: 959px) {
  .my-collection-page .section-header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 12px;
  }

  .my-collection-page .section-header-left {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
  }

  .my-collection-page .section-header-left input {
    width: 100%;
    min-width: 0;
    height: 48px;
    min-height: 48px;
    box-sizing: border-box;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 16px;
    line-height: 1.5;
  }

  .my-collection-page .section-header-right {
    flex: 0 0 clamp(144px, 34vw, 220px);
    width: clamp(144px, 34vw, 220px);
    min-width: 0;
    display: flex;
    align-items: center;
    align-self: center;
  }

  .my-collection-page :deep(.order-by-select),
  .my-collection-page :deep(.order-by-select .form-group),
  .my-collection-page :deep(.order-by-select .multiselect) {
    width: 100%;
    min-width: 0;
  }

  .my-collection-page :deep(.order-by-select .multiselect__tags) {
    height: 48px;
    min-height: 48px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding: 12px 40px 12px 16px;
    border-radius: 10px;
  }

  .my-collection-page :deep(.order-by-select .multiselect__single) {
    min-width: 0;
    max-width: 100%;
    font-size: 16px;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .my-collection-page :deep(.order-by-select .multiselect__option) {
    min-height: 48px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding: 12px 16px;
    font-size: 16px;
  }

  .my-collection-page :deep(.app-modal) {
    width: 100% !important;
    max-width: 100% !important;
    max-height: 90vh !important;
    border-radius: 16px;
  }

  .my-collection-page :deep(.app-modal-overlay) {
    align-items: flex-start;
    padding: calc(16px + env(safe-area-inset-top)) 12px 16px;
  }

  .my-collection-page .collection-add-button {
    min-width: 44px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (max-width: 576px) {
  .my-collection-page :deep(.page-header-block) {
    flex-wrap: nowrap;
    gap: 8px;
  }

  .my-collection-page :deep(.page-header-block__left) {
    min-width: 0;
  }

  .my-collection-page :deep(.page-header-block__title) {
    min-width: 0;
    font-size: 16px;
    gap: 6px;
  }

  .my-collection-page :deep(.page-header-block__right) {
    width: auto;
    flex-shrink: 0;
    gap: 8px;
    justify-content: flex-end;
  }

  .my-collection-page .collection-add-button {
    min-height: 44px;
    padding: 10px 12px;
    gap: 6px;
    font-size: 14px;
  }

  .my-collection-page .collection-add-button i {
    font-size: 16px;
  }

  .my-collection-page .section-header {
    gap: 8px;
  }

  .my-collection-page .section-header-right {
    flex-basis: 144px;
    width: 144px;
  }
}
</style>
